import {useCallback, useEffect, useState} from 'react';
import {TicketAssociation, useGroupByNameOrId, useUserInfo, ViewableByType} from '../../..';

export const useAllowedAndDefaultViewableBy = (draftAssociation?: Partial<TicketAssociation>) => {
  const {isErpUser, isMerchant} = useUserInfo();
  const [viewableBy, setViewableBy] = useState(ViewableByType.all);
  const [allowedViewableBy, setAllowedViewableBy] = useState<ViewableByType[]>([
    ViewableByType.agentOrErp,
    ViewableByType.all,
  ]);

  const [groupNameOrIdArgs, setGroupNameOrIdArgs] = useState<{
    nameOrId?: string;
    type?: 'name' | 'id';
    skip?: boolean;
  }>({skip: true});
  const group = useGroupByNameOrId(groupNameOrIdArgs);

  useEffect(() => {
    if (draftAssociation?.merchant?.group) {
      setGroupNameOrIdArgs({nameOrId: draftAssociation.merchant.group, type: 'name' as const});
    } else if (draftAssociation?.group?.id) {
      setGroupNameOrIdArgs({nameOrId: draftAssociation.group.id, type: 'id' as const});
    } else {
      setGroupNameOrIdArgs({skip: true});
    }
  }, [draftAssociation?.merchant?.group, draftAssociation?.group?.id]);
  const getDefaultViewableAndSetAllowedViewableBy = useCallback(
    (isDefaultAgentOnly: boolean) => {
      // Handle ERP user cases
      if (isErpUser) {
        switch (draftAssociation?.type) {
          case 'ERP User':
            setAllowedViewableBy([ViewableByType.internalOnly]);
            return ViewableByType.internalOnly;

          case 'Merchant':
            setAllowedViewableBy([
              ViewableByType.agentOrErp,
              ViewableByType.internalOnly,
              ViewableByType.all,
            ]);
            return isDefaultAgentOnly ? ViewableByType.agentOrErp : ViewableByType.all;
          case 'Agent':
            setAllowedViewableBy([
              ViewableByType.agentOrErp,
              ViewableByType.internalOnly,
              ViewableByType.all,
            ]);
            return ViewableByType.agentOrErp;
          case 'Application':
            setAllowedViewableBy([
              ViewableByType.agentOrErp,
              ViewableByType.internalOnly,
              ViewableByType.all,
            ]);
            return ViewableByType.agentOrErp;
        }
      }

      // Handle non-ERP user cases
      switch (draftAssociation?.type) {
        case 'ERP User':
          setAllowedViewableBy([ViewableByType.internalOnly, ViewableByType.agentOrErp]);
          return ViewableByType.internalOnly;

        case 'Agent':
          setAllowedViewableBy([ViewableByType.agentOrErp]);
          return ViewableByType.agentOrErp;

        case 'Merchant':
          setAllowedViewableBy([ViewableByType.agentOrErp, ViewableByType.all]);
          return isDefaultAgentOnly ? ViewableByType.agentOrErp : ViewableByType.all;

        case 'Application':
          setAllowedViewableBy([ViewableByType.agentOrErp, ViewableByType.all]);
          return ViewableByType.agentOrErp;
      }

      return ViewableByType.all;
    },
    [isErpUser, draftAssociation?.type]
  );

  useEffect(() => {
    const isDefaultAgentOnly =
      !group?.data?.applicationSettings?.newlyCreatedTicketsViewableByNotAgentOnly;

    const defaultViewableBy = getDefaultViewableAndSetAllowedViewableBy(isDefaultAgentOnly);
    setViewableBy(defaultViewableBy);
  }, [group, getDefaultViewableAndSetAllowedViewableBy, draftAssociation, isErpUser, isMerchant]);

  return {
    viewableBy,
    setViewableBy,
    allowedViewableBy,
  };
};
